/**
 * authConfig — user whitelist and role mapping.
 *
 * Authentication is handled by Supabase Google OAuth.
 * This module provides role mapping, authorisation checks,
 * and multi-role detection based on the user's email address.
 *
 * Roles are NEVER stored in the database — they are derived from the
 * authenticated email on every load. There are three roles:
 *   - 'adviser'                — Marinus
 *   - 'assistant'              — display label "Administration Assistant" (Wouter)
 *   - 'new_business_assistant' — display label "New Business Assistant" (Vicky)
 *
 * The internal id 'assistant' is intentionally kept (rather than renamed to
 * 'administration_assistant') because it is referenced across routing, nav
 * gating, the handover-memo PDF, the concurrency hook and pipelineService — a
 * rename would be a large, risky change for a purely cosmetic relabel. Only the
 * user-facing label changed; see ROLE_LABELS below.
 */

/**
 * Display labels for each internal role id. Kept in one place so the
 * user-facing vocabulary can never drift between the header, role chooser and
 * settings screens.
 */
export const ROLE_LABELS = {
  adviser: 'Adviser',
  assistant: 'Administration Assistant',
  new_business_assistant: 'New Business Assistant',
};

/**
 * Return the user-facing display label for an internal role id.
 */
export const getRoleLabel = (role) => ROLE_LABELS[role] || '';

/**
 * First name of the person who holds a role, for note-acknowledgement copy
 * ("Awaiting Wouter", "✓ Read by Marinus"). There is no profiles table, so
 * this mirrors the EMAIL_TO_FIRST_NAME convention used in the views. Roles
 * without a dedicated occupant yet (New Business Assistant until Vicky is
 * onboarded) fall back to the role label.
 */
const ROLE_HOLDER_FIRST_NAMES = {
  adviser: 'Marinus',
  assistant: 'Wouter',
};
export const getRoleHolderName = (role) =>
  ROLE_HOLDER_FIRST_NAMES[role] || ROLE_LABELS[role] || 'the other party';

/**
 * First name of the signed-in user, for note attribution and system-note
 * copy ("File drawn by Wouter. Work has commenced."). The two configured
 * logins (REACT_APP_MARINUS_EMAIL / REACT_APP_WOUTER_EMAIL) resolve to the
 * role holders' first names above; any other address falls back to its
 * local part, and an empty value to 'User'. This is the single first-name
 * helper — it replaced the per-view EMAIL_TO_FIRST_NAME maps in FileDetail
 * and PrepRequestDetail (decomposition step F2).
 */
export const getFirstName = (email) => {
  const normalised = (email || '').toLowerCase();
  const adviserEmail = (process.env.REACT_APP_MARINUS_EMAIL || '').toLowerCase();
  const assistantEmail = (process.env.REACT_APP_WOUTER_EMAIL || '').toLowerCase();
  if (normalised && normalised === adviserEmail) return ROLE_HOLDER_FIRST_NAMES.adviser;
  if (normalised && normalised === assistantEmail) return ROLE_HOLDER_FIRST_NAMES.assistant;
  return email ? email.split('@')[0] : 'User';
};

/**
 * List of authorised email addresses that may access Inbox.
 * Loaded from environment variable (comma-separated).
 */
export const getAllowedUsers = () => {
  const users = process.env.REACT_APP_ALLOWED_USERS || '';
  return users.split(',').map((email) => email.trim().toLowerCase()).filter(Boolean);
};

/**
 * Check whether a given email address is authorised to use Inbox.
 */
export const isUserAuthorised = (email) => {
  if (!email) return false;
  return getAllowedUsers().includes(email.toLowerCase());
};

/**
 * Determine the user's role based on their email address.
 * Returns 'adviser' for Marinus, 'assistant' for Wouter.
 */
export const getUserRole = (email) => {
  if (!email) return null;
  const normalised = email.toLowerCase();
  const adviserEmail = (process.env.REACT_APP_MARINUS_EMAIL || '').toLowerCase();
  const assistantEmail = (process.env.REACT_APP_WOUTER_EMAIL || '').toLowerCase();

  if (normalised === adviserEmail) return 'adviser';
  if (normalised === assistantEmail) return 'assistant';
  return null;
};

/**
 * Check whether a user can choose their role (multi-role user).
 * The adviser email can operate as either Adviser or Assistant.
 */
export const canChooseRole = (email) => {
  if (!email) return false;
  const normalised = email.toLowerCase();
  const adviserEmail = (process.env.REACT_APP_MARINUS_EMAIL || '').toLowerCase();
  return normalised === adviserEmail;
};

/**
 * Vicky Bridges's Google LOGIN email. Her role mapping keys off this address.
 *
 * Note the two-email pattern (same as Wouter): she LOGS IN with this Gmail
 * address, but NOTIFICATIONS go to her work address via the
 * REACT_APP_VICKY_EMAIL env var (read in mailApi.js). This login literal is
 * deliberately a code constant, matching the EMAIL_TO_NAME maps and the RLS
 * policy literals already in the repo — there is no login-email env var for
 * her, and REACT_APP_VICKY_EMAIL is specifically the notification address.
 * She must ALSO be present in REACT_APP_ALLOWED_USERS to get past the
 * whitelist at all (isUserAuthorised above).
 */
export const VICKY_LOGIN_EMAIL = 'bridgesvix@gmail.com';

/**
 * Return the array of roles available to a user.
 *
 * Marinus (the adviser email) keeps ALL THREE roles — Vicky's addition is
 * additive, and he continues to self-test and step in as needed. Vicky
 * (VICKY_LOGIN_EMAIL) is the production New Business Assistant, single role.
 * All other authorised users (i.e. Wouter) default to Administration
 * Assistant only.
 */
export const getUserRoles = (email) => {
  if (!email) return [];
  const normalised = email.toLowerCase();
  const adviserEmail = (process.env.REACT_APP_MARINUS_EMAIL || '').toLowerCase();
  if (adviserEmail && normalised === adviserEmail) {
    return ['adviser', 'assistant', 'new_business_assistant'];
  }
  // Vicky's check must run BEFORE the authorised-users fallback below,
  // otherwise she would be swept into the default Administration Assistant
  // mapping along with Wouter.
  if (normalised === VICKY_LOGIN_EMAIL) {
    return isUserAuthorised(email) ? ['new_business_assistant'] : [];
  }
  if (isUserAuthorised(email)) return ['assistant'];
  return [];
};
